import { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { X, Search, Printer, BookOpen, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  htmlToMarkdown, 
  parseMarkdownTOC, 
  searchInMarkdown 
} from '@/lib/markdown-utils';
import { useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts';
import { ManualContent } from './ManualContent';
import { ManualTOC } from './ManualTOC';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ManualData {
  id: string;
  title: string;
  content: string;
  category: string;
  created_at?: string;
  updated_at?: string;
  systems?: {
    id: string;
    name: string;
    code: string;
  };
}

interface ManualsModalProps {
  isOpen: boolean;
  onClose: () => void;
  manuals: ManualData[];
  defaultManualId?: string;
}

export function ManualsModal({ isOpen, onClose, manuals, defaultManualId }: ManualsModalProps) {
  const [activeManual, setActiveManual] = useState<ManualData | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeAnchor, setActiveAnchor] = useState<string>();
  const [isMobile, setIsMobile] = useState(false);
  const [mobileView, setMobileView] = useState<'list' | 'content'>('list');
  
  const searchInputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Check mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Convert content to markdown
  const manualsWithMarkdown = useMemo(() => {
    return manuals.map(manual => ({
      ...manual,
      markdownContent: htmlToMarkdown(manual.content),
    }));
  }, [manuals]);

  // Filter manuals by search
  const filteredManuals = useMemo(() => {
    if (!searchQuery.trim()) return manualsWithMarkdown;
    
    const query = searchQuery.toLowerCase();
    return manualsWithMarkdown.filter(manual => 
      manual.title.toLowerCase().includes(query) ||
      manual.category.toLowerCase().includes(query) ||
      manual.systems?.name.toLowerCase().includes(query) ||
      searchInMarkdown(manual.markdownContent, query)
    );
  }, [manualsWithMarkdown, searchQuery]);

  // Generate TOC for active manual
  const toc = useMemo(() => {
    if (!activeManual) return [];
    const markdown = htmlToMarkdown(activeManual.content);
    return parseMarkdownTOC(markdown);
  }, [activeManual]);

  // Initialize active manual from URL params or default
  useEffect(() => {
    if (!isOpen || manuals.length === 0) return;

    const params = new URLSearchParams(window.location.search);
    const manualParam = params.get('manual');
    const sectionParam = params.get('section');
    
    const lastManualId = localStorage.getItem('manual:lastId');
    const initialManualId = manualParam || defaultManualId || lastManualId || manuals[0]?.id;
    
    const manual = manuals.find(m => m.id === initialManualId) || manuals[0];
    if (manual) {
      setActiveManual(manual);
      
      // Scroll to section if specified
      if (sectionParam) {
        setTimeout(() => {
          const element = document.getElementById(sectionParam);
          element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setActiveAnchor(sectionParam);
        }, 100);
      }
    }
  }, [isOpen, manuals, defaultManualId]);

  // Handle manual selection
  const handleSelectManual = (manual: ManualData) => {
    setActiveManual(manual);
    localStorage.setItem('manual:lastId', manual.id);
    
    // Update URL
    const url = new URL(window.location.href);
    url.searchParams.set('manual', manual.id);
    url.searchParams.delete('section');
    window.history.pushState({}, '', url);
    
    // Switch to content view on mobile
    if (isMobile) {
      setMobileView('content');
    }
    
    // Reset scroll
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  };

  // Handle TOC click
  const handleTOCClick = (anchor: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('section', anchor);
    window.history.pushState({}, '', url);
    
    const element = document.getElementById(anchor);
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveAnchor(anchor);
  };

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  // Keyboard shortcuts
  useKeyboardShortcuts({
    escape: onClose,
    search: () => searchInputRef.current?.focus(),
    navigate: (direction) => {
      const newIndex = Math.max(0, Math.min(filteredManuals.length - 1, selectedIndex + direction));
      setSelectedIndex(newIndex);
    },
    select: () => {
      if (filteredManuals[selectedIndex]) {
        handleSelectManual(filteredManuals[selectedIndex]);
      }
    },
  }, isOpen);

  // Block body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const activeMarkdown = activeManual ? htmlToMarkdown(activeManual.content) : '';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-[min(1400px,96vw)] h-[92vh] p-0 gap-0 flex flex-col"
        aria-label="Modal de Manuais"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 sm:px-6 py-3 shrink-0">
          <div className="flex items-center gap-3">
            <BookOpen className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Manuais</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className="hidden sm:flex"
            >
              <Printer className="h-4 w-4 mr-2" />
              Imprimir
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Fechar (Esc)</span>
            </Button>
          </div>
        </div>

        {/* Mobile Tabs */}
        {isMobile ? (
          <Tabs value={mobileView} onValueChange={(v) => setMobileView(v as 'list' | 'content')} className="flex-1 flex flex-col">
            <TabsList className="w-full rounded-none border-b">
              <TabsTrigger value="list" className="flex-1">Lista</TabsTrigger>
              <TabsTrigger value="content" className="flex-1">Conteúdo</TabsTrigger>
            </TabsList>
            
            <TabsContent value="list" className="flex-1 mt-0">
              <div className="p-3">
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    ref={searchInputRef}
                    placeholder="Buscar manuais... (Ctrl+K)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <ScrollArea className="h-[calc(92vh-140px)]">
                <div className="px-3 pb-3 space-y-2">
                  {filteredManuals.map((manual, index) => (
                    <button
                      key={manual.id}
                      onClick={() => handleSelectManual(manual)}
                      className={cn(
                        "w-full text-left rounded-lg px-3 py-2 transition-colors hover:bg-muted",
                        activeManual?.id === manual.id && "bg-muted ring-1 ring-primary",
                        selectedIndex === index && "ring-1 ring-muted-foreground"
                      )}
                    >
                      <p className="font-medium text-sm line-clamp-2">{manual.title}</p>
                      <div className="flex gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs">
                          {manual.category}
                        </Badge>
                        {manual.systems && (
                          <Badge variant="outline" className="text-xs">
                            {manual.systems.name}
                          </Badge>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </TabsContent>
            
            <TabsContent value="content" className="flex-1 mt-0">
              {activeManual ? (
                <ScrollArea className="h-[calc(92vh-100px)]">
                  <div className="p-4 sm:p-6 manual-content">
                    <div className="mb-6">
                      <h1 className="text-2xl font-bold mb-2">{activeManual.title}</h1>
                      {activeManual.updated_at && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Atualizado em {format(new Date(activeManual.updated_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                        </p>
                      )}
                    </div>
                    <Separator className="mb-6" />
                    <ManualContent 
                      content={activeMarkdown} 
                      manualId={activeManual.id}
                    />
                  </div>
                </ScrollArea>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  Selecione um manual na lista
                </div>
              )}
            </TabsContent>
          </Tabs>
        ) : (
          /* Desktop Layout */
          <div className="grid grid-cols-[280px_1fr] flex-1 overflow-hidden">
            {/* Left Column - Navigation */}
            <div className="border-r flex flex-col manual-sidebar">
              <div className="p-3 border-b">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    ref={searchInputRef}
                    placeholder="Buscar... (Ctrl+K)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <ScrollArea className="flex-1">
                <div className="p-3 space-y-2">
                  {filteredManuals.map((manual, index) => (
                    <button
                      key={manual.id}
                      onClick={() => handleSelectManual(manual)}
                      className={cn(
                        "w-full text-left rounded-lg px-3 py-2 transition-colors hover:bg-muted",
                        activeManual?.id === manual.id && "bg-muted ring-1 ring-primary",
                        selectedIndex === index && "ring-1 ring-muted-foreground"
                      )}
                    >
                      <p className="font-medium text-sm line-clamp-2">{manual.title}</p>
                      <div className="flex gap-2 mt-1 flex-wrap">
                        <Badge variant="secondary" className="text-xs">
                          {manual.category}
                        </Badge>
                        {manual.systems && (
                          <Badge variant="outline" className="text-xs">
                            {manual.systems.name}
                          </Badge>
                        )}
                      </div>
                    </button>
                  ))}
                  {filteredManuals.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      Nenhum manual encontrado
                    </p>
                  )}
                </div>
              </ScrollArea>
            </div>

            {/* Right Column - Content */}
            <div className="flex overflow-hidden">
              <ScrollArea className="flex-1" ref={contentRef}>
                {activeManual ? (
                  <div className="p-4 sm:p-6 manual-content" data-manual-title={activeManual.title}>
                    <div className="mb-6">
                      <h1 className="text-2xl font-bold mb-2">{activeManual.title}</h1>
                      {activeManual.updated_at && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Atualizado em {format(new Date(activeManual.updated_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                        </p>
                      )}
                    </div>
                    <Separator className="mb-6" />
                    <ManualContent 
                      content={activeMarkdown} 
                      manualId={activeManual.id}
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    <div className="text-center">
                      <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Selecione um manual na lista</p>
                    </div>
                  </div>
                )}
              </ScrollArea>

              {/* TOC Sidebar (XL screens) */}
              {toc.length > 0 && activeManual && (
                <div className="hidden xl:block w-64 border-l manual-toc-sidebar">
                  <div className="sticky top-0 p-4">
                    <ManualTOC 
                      items={toc}
                      onItemClick={handleTOCClick}
                      activeAnchor={activeAnchor}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Print Styles */}
        <style>{`
          @media print {
            .manual-sidebar,
            .manual-header-actions,
            .manual-toc-sidebar {
              display: none !important;
            }
            
            .manual-content {
              max-width: 100% !important;
            }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}
